import { vettingPath } from '../common-flow/entries/path';
import { FR_DOCUMENTS, applicableDocuments } from '../country-specific-flows/fr-flow/frDocuments';
import {
  CharityTypeEnum,
  CountryCodeEnum,
  DonationPreferencesEnum,
  ORGANISATION_OTHER_OPTIONS_REQUIRING_AGREMENT,
  OrganisationPurposeEnum,
  OrganisationSpecificTypeEnum,
  type CharityVettingFormValues,
  type CountryCode,
  type OrganisationOtherOptionEnum,
} from '../types/charityVettingTypes';

/**
 * The single source of truth for how the vetting wizard advances.
 *
 * Each entry maps a step's route segment to a resolver that returns the NEXT
 * segment given the answers so far — i.e. it simulates the user walking the
 * flow. Both the in-session Next button (useCharityVettingStepper) and the
 * resume logic (resolveResumeStep) derive navigation from this map, so branch
 * behavior can never diverge between the two.
 *
 * Keyed by route segment (not a step "name") because segments are unique while
 * the FR food-safety interstitials share a step name.
 */

type CharityOnboarding = Partial<CharityVettingFormValues['charityOnboarding']>;

type FlowResolver = (charityOnboarding: CharityOnboarding, country: CountryCode) => string | null;

export const FLOW_END = null;

const hasPli = (charityOnboarding: CharityOnboarding) =>
  charityOnboarding.hasPli ?? charityOnboarding.pliCover?.value !== undefined;

const hasRgseaa = (charityOnboarding: CharityOnboarding) =>
  charityOnboarding.hasRgseaa ?? charityOnboarding.rgseaa != null;

const requiresAgrement = (option: OrganisationOtherOptionEnum | undefined) =>
  !!option && ORGANISATION_OTHER_OPTIONS_REQUIRING_AGREMENT.includes(option);

// Where the food branch continues after the accept-all / unwanted-food pages.
const foodBranchEntry = (country: CountryCode): string => {
  switch (country) {
    case CountryCodeEnum.ES:
      return vettingPath.rgseaaQuestionPath;
    case CountryCodeEnum.PT:
      return vettingPath.preparationKitchenPath;
    case CountryCodeEnum.GB:
      return vettingPath.foodHygienePath;
    case CountryCodeEnum.FR:
      return vettingPath.foodSafetyQThreePath;
    default:
      return vettingPath.socialAccountsPath;
  }
};

const documentsEntry: FlowResolver = (charityOnboarding, country) => {
  if (country !== CountryCodeEnum.FR) {
    return vettingPath.socialAccountsPath;
  }
  return applicableDocuments(charityOnboarding)[0]?.segment ?? vettingPath.socialAccountsPath;
};

const afterDocument =
  (segment: string): FlowResolver =>
  (charityOnboarding) => {
    const chain = applicableDocuments(charityOnboarding);
    const index = chain.findIndex((document) => document.segment === segment);
    if (index === -1) {
      return vettingPath.socialAccountsPath;
    }
    return chain[index + 1]?.segment ?? vettingPath.socialAccountsPath;
  };

const registrationNext: FlowResolver = (charityOnboarding, country) => {
  switch (country) {
    case CountryCodeEnum.PT:
      switch (charityOnboarding.charityType) {
        case CharityTypeEnum.VOLUNTEERS_ORG:
          return vettingPath.nifPath;
        case CharityTypeEnum.PRIVATE:
        case CharityTypeEnum.FOUNDATIONS_AND_PUBLIC_UTILITY_REGISTERED_ORG:
          return vettingPath.nipcPath;
        default:
          // NGO — and the convergence point of every PT branch.
          return vettingPath.fiscalBenefitsPath;
      }
    case CountryCodeEnum.ES:
      return vettingPath.cifPath;
    case CountryCodeEnum.PL:
      return vettingPath.publicBenefitPath;
    default:
      // UK
      return charityOnboarding.charityType === CharityTypeEnum.REGISTERED_CHARITY
        ? vettingPath.charityNumberPath
        : vettingPath.companyNumberPath;
  }
};

const donationPreferencesNext: FlowResolver = (charityOnboarding) => {
  switch (charityOnboarding.donationPreferences) {
    case DonationPreferencesEnum.NON_FOOD:
      return vettingPath.acceptAllNonFoodPath;
    case DonationPreferencesEnum.FOOD_AND_NON_FOOD:
      return vettingPath.acceptAllFoodAndNonFoodPath;
    case DonationPreferencesEnum.ONLY_FOOD:
      return vettingPath.acceptAllFoodsPath;
    default:
      return vettingPath.socialAccountsPath;
  }
};

const organisationSpecificTypeNext: FlowResolver = (charityOnboarding) => {
  switch (charityOnboarding.organisationSpecificType) {
    case OrganisationSpecificTypeEnum.ASSOCIATION_FONDATION_UTILITE_PUBLIQUE:
      return vettingPath.decreePublicationDatesPath;
    case OrganisationSpecificTypeEnum.ASSOCIATION_MOSELLE_ALSACE:
      return vettingPath.arretePublicationDatePath;
    case OrganisationSpecificTypeEnum.OTHER:
      return vettingPath.organisationSituationPath;
    default:
      return vettingPath.sirenPath;
  }
};

const nextStepBySegment: Record<string, FlowResolver> = {
  [vettingPath.getStartedPath]: () => vettingPath.searchPath,
  [vettingPath.searchPath]: () => vettingPath.contactDetailsPath,
  [vettingPath.contactDetailsPath]: () => vettingPath.legalRepresentativePath,
  [vettingPath.manualContactDetailsPath]: () => vettingPath.legalRepresentativePath,
  [vettingPath.legalRepresentativePath]: (_co, country) =>
    country === CountryCodeEnum.FR
      ? vettingPath.authorisedFoodAidPath
      : vettingPath.registrationPath,

  // Country-specific organisation/fiscal identity.
  [vettingPath.registrationPath]: registrationNext,
  // UK
  [vettingPath.charityNumberPath]: () => vettingPath.companyRegistrationPath,
  [vettingPath.companyRegistrationPath]: (charityOnboarding) =>
    charityOnboarding.isCompany ? vettingPath.companyNumberPath : vettingPath.pliPath,
  [vettingPath.companyNumberPath]: () => vettingPath.pliPath,
  [vettingPath.pliPath]: (charityOnboarding) =>
    hasPli(charityOnboarding) ? vettingPath.pliCoverPath : vettingPath.donationPreferencesPath,
  [vettingPath.pliCoverPath]: () => vettingPath.donationPreferencesPath,
  // FR
  [vettingPath.authorisedFoodAidPath]: () => vettingPath.organisationPurposePath,
  [vettingPath.organisationPurposePath]: (charityOnboarding) =>
    charityOnboarding.organisationPurpose === OrganisationPurposeEnum.GENERAL_INTEREST
      ? vettingPath.organisationSpecificTypePath
      : vettingPath.organisationOtherOptionPath,
  [vettingPath.organisationSpecificTypePath]: organisationSpecificTypeNext,
  [vettingPath.decreePublicationDatesPath]: () => vettingPath.sirenPath,
  [vettingPath.arretePublicationDatePath]: () => vettingPath.sirenPath,
  [vettingPath.organisationSituationPath]: () => vettingPath.sirenPath,
  [vettingPath.organisationOtherOptionPath]: (charityOnboarding) =>
    requiresAgrement(charityOnboarding.organisationOtherOption)
      ? vettingPath.agrementDatePath
      : vettingPath.sirenPath,
  [vettingPath.agrementDatePath]: () => vettingPath.sirenPath,
  [vettingPath.sirenPath]: () => vettingPath.donationPreferencesPath,
  // PT
  [vettingPath.nifPath]: () => vettingPath.fiscalBenefitsPath,
  [vettingPath.nipcPath]: () => vettingPath.fiscalBenefitsPath,
  [vettingPath.fiscalBenefitsPath]: () => vettingPath.donationPreferencesPath,
  // ES
  [vettingPath.cifPath]: () => vettingPath.donationCertificatePath,
  [vettingPath.donationCertificatePath]: () => vettingPath.donationPreferencesPath,
  // PL
  [vettingPath.publicBenefitPath]: () => vettingPath.krsPath,
  [vettingPath.krsPath]: () => vettingPath.nipPath,
  [vettingPath.nipPath]: () => vettingPath.donationPreferencesPath,

  // Donation preferences and the food/non-food branch.
  [vettingPath.donationPreferencesPath]: donationPreferencesNext,
  [vettingPath.acceptAllFoodsPath]: (charityOnboarding, country) =>
    charityOnboarding.acceptsAllFood ? foodBranchEntry(country) : vettingPath.unwantedFoodPath,
  [vettingPath.acceptAllFoodAndNonFoodPath]: (charityOnboarding, country) =>
    charityOnboarding.acceptsAllFoodAndNonFood
      ? foodBranchEntry(country)
      : vettingPath.unwantedFoodPath,
  [vettingPath.acceptAllNonFoodPath]: (charityOnboarding, country) =>
    charityOnboarding.acceptsAllNonFood
      ? documentsEntry(charityOnboarding, country)
      : vettingPath.unwantedFoodPath,
  [vettingPath.unwantedFoodPath]: (charityOnboarding, country) =>
    charityOnboarding.donationPreferences === DonationPreferencesEnum.NON_FOOD
      ? documentsEntry(charityOnboarding, country)
      : foodBranchEntry(country),

  // UK food hygiene.
  [vettingPath.foodHygienePath]: () => vettingPath.foodHygieneCertificatePath,
  [vettingPath.foodHygieneCertificatePath]: (charityOnboarding) =>
    charityOnboarding.hasHygieneCertificate
      ? vettingPath.SubmitFoodHygieneCertificatePath
      : vettingPath.socialAccountsPath,
  [vettingPath.SubmitFoodHygieneCertificatePath]: () => vettingPath.socialAccountsPath,

  [vettingPath.foodSafetyQThreePath]: () => vettingPath.foodSafetyManagementPlanDeclarationPath,
  [vettingPath.foodSafetyManagementPlanDeclarationPath]: () => vettingPath.haccpDeclarationPath,
  [vettingPath.haccpDeclarationPath]: documentsEntry,

  ...Object.fromEntries(
    FR_DOCUMENTS.map((document) => [document.segment, afterDocument(document.segment)]),
  ),

  // PT kitchen / HACCP, shared consumption-type.
  [vettingPath.preparationKitchenPath]: () => vettingPath.consumptionTypePath,
  [vettingPath.consumptionTypePath]: (_co, country) =>
    country === CountryCodeEnum.PT ? vettingPath.haccpPath : vettingPath.socialAccountsPath,
  [vettingPath.haccpPath]: (charityOnboarding) =>
    charityOnboarding.haccp ? vettingPath.haccpCertificatePath : vettingPath.socialAccountsPath,
  [vettingPath.haccpCertificatePath]: () => vettingPath.socialAccountsPath,

  // ES RGSEAA.
  [vettingPath.rgseaaQuestionPath]: (charityOnboarding) =>
    hasRgseaa(charityOnboarding) ? vettingPath.rgseaaPath : vettingPath.consumptionTypePath,
  [vettingPath.rgseaaPath]: () => vettingPath.consumptionTypePath,

  // Tail.
  [vettingPath.socialAccountsPath]: () => vettingPath.reviewDetailsPath,
  [vettingPath.reviewDetailsPath]: () => FLOW_END,
};

/**
 * The next route segment after `segment` for a charity with these answers.
 * Returns null when `segment` is the last step, and undefined when `segment`
 * is not a wizard step.
 */
export const getNextStepSegment = (
  segment: string | undefined,
  charityOnboarding: CharityOnboarding,
  country: CountryCode,
): string | null | undefined => {
  if (!segment) {
    return undefined;
  }
  return nextStepBySegment[segment]?.(charityOnboarding, country);
};

/**
 * The ordered segments a charity with these answers walks through, starting
 * from `from` (inclusive) until the end of the flow. The visited-set guard
 * makes a definition bug (a cycle) terminate instead of hanging.
 */
export const getFlowSequence = (
  charityOnboarding: CharityOnboarding,
  country: CountryCode,
  from: string = vettingPath.searchPath,
): string[] => {
  const sequence: string[] = [];
  const visited = new Set<string>();
  let segment: string | null | undefined = from;
  while (segment && !visited.has(segment)) {
    visited.add(segment);
    sequence.push(segment);
    segment = getNextStepSegment(segment, charityOnboarding, country);
  }
  return sequence;
};
